import { useState } from 'react';
import { Card, Button, Typography, message } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import { useSubscription } from '../../hooks/use_subscription';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useApi } from '../../hooks/use_api';
import getEnv from '../../env';

const { Title, Text } = Typography;

/**
 * Superadmin-only panel for the node's subscription (#38): jointly managed by
 * super admins, billed once for the whole node rather than per user. Renders
 * nothing for non-superadmins (useSuperAdmins' list endpoint 403s), nor on a
 * node with the feature_subscription toggle off (#36) — where the billing
 * routes behind these buttons aren't mounted — so it is safe to mount
 * unconditionally.
 */
function NodeSubscription() {
    const { isDarkMode } = useTheme();
    const logoSrc = isDarkMode ? '/white_outline.svg' : '/black_outline.svg';
    const api = useApi();
    const subscription = useSubscription();
    const { isServerAdmin: isSuperAdmin } = useSuperAdmins();
    const { featureSubscription } = useNodeInfo();
    const [billingLoading, setBillingLoading] = useState(false);
    const userId = localStorage.getItem('id');

    if (!isSuperAdmin || !featureSubscription) return null;

    const handleUpgrade = async () => {
        const priceId = getEnv('REACT_APP_STRIPE_PRICE_ID_PRO');
        if (!priceId) { message.error('Subscription not configured'); return; }
        setBillingLoading(true);
        try {
            const data = await api.post('/api/billing/checkout', {
                user_id: userId,
                price_id: priceId,
                success_url: `${window.location.origin}/account?subscribed=true`,
                cancel_url: `${window.location.origin}/account`,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
            else message.error('Could not start checkout');
        } catch {
            message.error('Checkout failed');
        } finally {
            setBillingLoading(false);
        }
    };

    const handleManage = async () => {
        setBillingLoading(true);
        try {
            const data = await api.post('/api/billing/portal', {
                user_id: userId,
                return_url: `${window.location.origin}/account`,
            });
            if (data.portal_url) window.location.href = data.portal_url;
            else message.error('Could not open portal');
        } catch {
            message.error('Portal failed');
        } finally {
            setBillingLoading(false);
        }
    };

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Node subscription</Title>
            <Text type="secondary">
                This subscription covers the whole node, not your individual account.
                It is billed once and jointly managed by all super admins.
            </Text>
            <div style={{ marginTop: 16 }}>
                {subscription.is_subscriber ? (
                    <Button size="small" onClick={handleManage} loading={billingLoading}>
                        Manage subscription
                    </Button>
                ) : (
                    <Button
                        type="default"
                        icon={<img src={logoSrc} alt="" style={{ width: 14, height: 14 }} />}
                        onClick={handleUpgrade}
                        loading={billingLoading}
                    >
                        Start Subscription
                    </Button>
                )}
            </div>
        </Card>
    );
}

export default NodeSubscription;
